/**
 * Générateur — O9 : calculer une quatrième proportionnelle par la procédure de
 * son choix.
 * Attendu ID : 172 | Niveau : 4ème | Domaine : proportionnalite
 *
 * Banque de contextes (tableau de proportionnalité, problème, échelle,
 * pourcentage, procédures, relier). Toutes les valeurs sont EXACTES par
 * construction (rationnels de proportionnalite.js) et chaque réponse est
 * recalculée par un second chemin dans le gen (throw sinon). Aucun solveur
 * test-side ne couvre 172 ; le test jetable tmp-revue-172 relit les nombres
 * dans le texte affiché.
 *
 * Revue 2026-09-23 (vague 3), décisions du coordinateur appliquées en mode
 * auto. Attendu de fin de 4e (Eduscol, p. 6) : « Il calcule une quatrième
 * proportionnelle par la procédure de son choix. » Exemples de réussite :
 * « il calcule une quatrième proportionnelle par différentes procédures (un
 * pourcentage, une échelle…) » ; « Sachant que huit briques de masse identique
 * pèsent 13,6 kg, calcule la masse de six de ces briques », avec les
 * procédures « en calculant la masse d'une brique, puis en la multipliant par
 * 6 », « à l'aide d'un tableau en calculant le coefficient de
 * proportionnalité », « la somme de la masse de deux briques et de la masse de
 * quatre briques, ou la différence de la masse de huit briques et de la masse
 * de deux briques », « directement : 6 × 13,6 : 8 ». Cours (19Proport1) :
 * tableau, coefficient, produits en croix, « x = 5 × 2 : 4 = 2,5 », farine
 * 3 € pour 2,5 kg, 1,8 kg à 2,16 € ; la division s'écrit « : » (KaTeX).
 *   - Rappel brouillon IMPÉRATIF sur tous les formats (RAPPEL_BROUILLON),
 *     juste avant la question.
 *   - tableau_texte GARDÉ : tableau de 2 ou 3 colonnes (avant 4), le « ? »
 *     dans la ligne du bas quatre fois sur cinq, dans celle du haut sinon ;
 *     « (colonne de x) » et « (allure régulière) » retirés ; prix écrits avec
 *     deux décimales (« 5,40 »).
 *   - tableau_texte_trou GARDÉ : deux cases, étiquettes-pièges nommées
 *     additif (la case voisine plus l'écart de la ligne du haut, DEPP 4e 2023
 *     q. 4, 15,1 %) et recopie (le nombre de la ligne du haut, q. 5), plus
 *     aucune valeur déjà écrite dans la ligne du bas ; au moins deux
 *     affectations gardent la ligne croissante (rien ne se remplit par l'ordre
 *     seul) ; phrase en lignes KaTeX d'une case chacune, ancrées par leur
 *     colonne, moins de soixante caractères.
 *   - contexte_texte RECADRÉ : contextes plausibles (briques de l'exemple,
 *     farine de 1,20 € à 3,50 € le kilo, dont le prix du cours, recette pour
 *     p personnes, consommation d'essence de 5,5 à 8 L aux 100 km) ; la
 *     quantité cherchée n'est ni un multiple ni un diviseur de la donnée
 *     (avant : multiple dans 100 % des tirages), elle peut être plus petite ;
 *     données décimales à une décimale au plus (13,75 L et 5,25 € écartés,
 *     un prix s'écrit quand même avec ses centimes : 1,80 €) ; réponse
 *     décimale exacte à deux décimales au plus.
 *   - contexte_qcm_unique NOUVEAU (le QCM qui manquait à l'attendu, seul
 *     format dépouillable en classe) : le problème des briques et ses voisins,
 *     distracteurs nommés additif (principale), une_etape, somme_des_nombres,
 *     mauvais_diviseur ; contrat exporté. Ajout du correcteur : le rang de la
 *     clé parmi les quatre valeurs est visé uniformément (sans cela une_etape
 *     est toujours la plus petite, somme_des_nombres presque toujours la plus
 *     grande, et la clé tombait au milieu dans 85 % des tirages) ; la quantité
 *     cherchée est tirée au-dessous comme au-dessus de la donnée.
 *   - echelle_texte GARDÉ (jamais la notation 1/n, automatisme de 3e 1247).
 *   - pourcentage_texte RECADRÉ : p parmi 5, 15, 30, 40, 60, 75 (hors des
 *     automatismes 1128 et 1129) ; populations plausibles (plus de « ville de
 *     200 habitants ») ; AJOUT du sens « quel pourcentage ? » du cours, erreurs
 *     visées complementaire et nombre_pour_pourcentage (DEPP q. 18) tenues
 *     différentes de la réponse.
 *   - ratio_texte SORTI, mode auto, À ARBITRER : la notation « a : b » n'est ni
 *     dans l'attendu de fin de 4e ni dans le cours (le BO 2025 la met en
 *     objectif de 4e, à part).
 *   - procedures_qcm_multiple RECADRÉ : procédures sur le même moule, vraies
 *     et fausses (les bonnes n'étaient plus longues que par construction) ;
 *     deux à quatre bonnes, nombre annoncé ; plus de procédure circulaire
 *     (la masse retirée n'est jamais celle qu'on cherche) ; sous-masses
 *     faciles comme dans l'exemple ; aucune procédure fausse ne donne la bonne
 *     masse (recalcul exact, famille par famille) ; calculs en KaTeX avec « : ».
 *     Ajout du correcteur : les cinq options affichées sont re-choisies tant
 *     que les justes sont exactement les plus longues, ou les plus courtes, du
 *     texte lu ; la procédure additif s'écrit en deux temps comme les justes
 *     (« Calculer 8 − 6, puis retirer le résultat à 13,6. »).
 *   - quatrieme_relier RECADRÉ : plus de coefficient écrit dans l'énoncé, une
 *     colonne complète à la place (« 2 kg de pommes coûtent 5,40 € ») ; trois
 *     sources, cinq cibles dont deux leurres additif et recopie ; un leurre au
 *     moins tombe entre les vraies cibles, relier par le rang ne marche jamais ;
 *     « Deux prix sont en trop. ».
 *   - Exemple sacrifié du coup de pouce prévu au cadrage (le tableau du cours
 *     4 ; 5 ; 2 → 2,5) : exclu partout où il pourrait sortir.
 * Huit formats (avant : huit ; ratio sort, le QCM entre).
 */

import {
    randomChoice, randInt, shuffle, filtrerDistracteurs,
    makeSaisie, makeTexteTrou, makeQcm, makeRelier, tex,
} from '../../../utils';
import {
    ratFromStr, ratMul, ratDiv, ratAdd, ratSub, ratEq, ratN, ratToDecFr, ratVal,
    nbDecimales, formatFr, euros, decTex, tableEnonce, ligneTable,
} from './proportionnalite';

const ATTENDU_ID = 172;
const L_QUATRIEME = 'Calculer une quatrième proportionnelle';
const opts = (label) => ({ niveau: '4eme', matiere: 'maths', attendu: ATTENDU_ID, label });

// Rappel brouillon (décision 2026-09-23), IMPÉRATIF sur tous les formats : la
// quatrième proportionnelle de l'exemple (6 × 13,6 : 8) se pose à la main.
// Forme maison du parc (gen_149), juste avant la question, sans méthode.
const RAPPEL_BROUILLON = '<p class="italic text-slate-400">✏️ Pose tes calculs au brouillon.</p>';

const R = (s) => ratFromStr(String(s));
/** Écriture d'une valeur selon son unité : deux décimales pour un prix. */
const ecrire = (r, unite) => (unite === '€' ? euros(r) : formatFr(ratToDecFr(r)));
/** Nombre de l'énoncé, déjà écrit en français. */
const nb = (r) => formatFr(ratToDecFr(r));
const T = (s) => `\\text{${s}}`;
// Accord du verbe avec une quantité décimale : singulier sous 2 (« Combien coûte
// 1,8 kg ? », cours 19Proport1), pluriel à partir de 2.
const coute = (r) => (ratVal(r) < 2 ? 'coûte' : 'coûtent');

// Tableau du cours (19Proport1 : 4 ; 5 ; 2 → 2,5), réservé au coup de pouce.
const exempleDuCours = (connus) => {
    const v = connus.map(ratVal).sort((a, b) => a - b);
    return v.length === 3 && v[0] === 2 && v[1] === 4 && v[2] === 5;
};

// ═══════════════════════════════════════════════════════════
// Contextes des tableaux et du relier
// ═══════════════════════════════════════════════════════════

const CONTEXTES_TABLEAU = [
    {
        id: 'pommes', phrase: 'Le prix des pommes est proportionnel à la masse achetée.',
        a: 'Masse (kg)', b: 'Prix (€)', ua: 'kg', ub: '€',
        coeffs: ['2,4', '2,8', '3,2', '1,8'], vals: ['0,5', '1,5', '2', '2,5', '3', '4', '5'],
        ancre: (x) => `${T('Pour ')}${x}${T(' kg : ')}`,
        colonne: (x, y, v) => `${x} kg de pommes ${coute(v)} ${y} €.`,
        relie: 'Relie chaque masse de pommes à son prix. Deux prix sont en trop.',
    },
    {
        id: 'marche', phrase: 'À allure régulière, la distance parcourue est proportionnelle à la durée de la marche.',
        a: 'Durée (min)', b: 'Distance (m)', ua: 'min', ub: 'm',
        coeffs: ['70', '75', '80', '90'], vals: ['4', '6', '10', '15', '20', '25', '30'],
        ancre: (x) => `${T('En ')}${x}${T(' min : ')}`,
        colonne: (x, y) => `À allure régulière, un randonneur parcourt ${y} m en ${x} min.`,
        relie: 'Relie chaque durée à la distance parcourue. Deux distances sont en trop.',
    },
    {
        id: 'recette', phrase: 'La masse de farine est proportionnelle au nombre de parts de gâteau.',
        a: 'Nombre de parts', b: 'Farine (g)', ua: 'parts', ub: 'g',
        coeffs: ['35', '40', '45', '60'], vals: ['2', '4', '6', '8', '10', '12'],
        ancre: (x) => `${T('Pour ')}${x}${T(' parts : ')}`,
        colonne: (x, y) => `Pour ${x} parts de gâteau, il faut ${y} g de farine.`,
        relie: 'Relie chaque nombre de parts à la masse de farine nécessaire. Deux masses sont en trop.',
    },
    {
        id: 'tissu', phrase: 'Le prix du tissu est proportionnel à la longueur achetée.',
        a: 'Longueur (m)', b: 'Prix (€)', ua: 'm', ub: '€',
        coeffs: ['6,5', '8,4', '12,5', '9,6'], vals: ['0,5', '1,5', '2', '2,5', '3', '4'],
        ancre: (x) => `${T('Pour ')}${x}${T(' m : ')}`,
        colonne: (x, y, v) => `${x} m de tissu ${coute(v)} ${y} €.`,
        relie: 'Relie chaque longueur de tissu à son prix. Deux prix sont en trop.',
    },
];

/** nCols valeurs du haut (triées), celles du bas = coefficient × haut, exactes. */
const construireTableau = (ctx, nCols) => {
    const k = R(randomChoice(ctx.coeffs));
    const haut = shuffle(ctx.vals).slice(0, nCols).map(R).sort((u, v) => ratVal(u) - ratVal(v));
    const bas = haut.map((v) => ratMul(k, v));
    if (bas.some((v) => nbDecimales(v) > 2)) return null;
    return { k, haut, bas };
};

// ═══════════════════════════════════════════════════════════
// 1. TABLEAU — un « ? » dans un tableau de 2 ou 3 colonnes (saisie)
// ═══════════════════════════════════════════════════════════

const genTableauSaisie = () => {
    for (let essai = 0; essai < 80; essai++) {
        const ctx = randomChoice(CONTEXTES_TABLEAU);
        const nCols = randomChoice([2, 3]);
        const t = construireTableau(ctx, nCols);
        if (!t) continue;
        const col = randInt(0, nCols - 1);
        const enHaut = randInt(0, 4) === 0;
        const cherche = enHaut ? t.haut[col] : t.bas[col];
        const connus = [...t.haut, ...t.bas].filter((v, i) => i !== (enHaut ? col : nCols + col));
        if (exempleDuCours(connus)) continue;
        // la valeur cherchée ne doit pas déjà être écrite dans le tableau
        if (connus.some((v) => ratEq(v, cherche))) continue;
        // second chemin : quatrième proportionnelle par produit en croix sur une autre colonne
        const j = col === 0 ? 1 : 0;
        const verif = enHaut ? ratDiv(ratMul(t.haut[j], t.bas[col]), t.bas[j]) : ratDiv(ratMul(t.bas[j], t.haut[col]), t.haut[j]);
        if (!ratEq(verif, cherche)) throw new Error('gen_172 tableau : quatrième proportionnelle incohérente');
        const cellule = (v, ligne, i) => ((ligne === (enHaut ? 'haut' : 'bas') && i === col) ? '<strong>?</strong>' : ecrire(v, ligne === 'haut' ? ctx.ua : ctx.ub));
        const tableau = tableEnonce(
            ligneTable(ctx.a, t.haut.map((v, i) => cellule(v, 'haut', i))),
            ligneTable(ctx.b, t.bas.map((v, i) => cellule(v, 'bas', i))),
        );
        const payload = makeSaisie(ecrire(cherche, enHaut ? ctx.ua : ctx.ub),
            `<p>${ctx.phrase}</p>\n${tableau}`
            + RAPPEL_BROUILLON
            + '<p><b>Quelle valeur remplace le « ? » dans ce tableau de proportionnalité ?</b></p>',
            { ...opts(L_QUATRIEME), strict: false, suffixe: enHaut ? ctx.ua : ctx.ub });
        payload._meta.params = { contexte: ctx.id, nCols, enHaut, col };
        return payload;
    }
    throw new Error('gen_172 tableau : aucun tirage valide');
};

// ═══════════════════════════════════════════════════════════
// 2. TABLEAU À DEUX CASES — étiquettes additif et recopie (drag)
// ═══════════════════════════════════════════════════════════

/** Nombre d'affectations de deux étiquettes distinctes aux deux trous qui gardent la ligne croissante. */
const affectationsCroissantes = (bas, trous, etiquettes) => {
    let n = 0;
    etiquettes.forEach((e1) => etiquettes.forEach((e2) => {
        if (e1 === e2) return;
        const ligne = bas.map((v, i) => (i === trous[0] ? R(e1) : (i === trous[1] ? R(e2) : v)));
        if (ligne.every((v, i) => i === 0 || ratVal(v) > ratVal(ligne[i - 1]))) n++;
    }));
    return n;
};

const genTableauTrou = () => {
    for (let essai = 0; essai < 120; essai++) {
        const ctx = randomChoice(CONTEXTES_TABLEAU);
        const t = construireTableau(ctx, 4);
        if (!t) continue;
        const trous = shuffle([0, 1, 2, 3]).slice(0, 2).sort((u, v) => u - v);
        const connus = [0, 1, 2, 3].filter((i) => !trous.includes(i));
        const mots = trous.map((i) => ecrire(t.bas[i], ctx.ub));
        if (mots[0] === mots[1]) continue;
        // Étiquettes-pièges nommées : additif (colonne connue la plus proche + écart
        // de la ligne du haut), recopie (le nombre de la ligne du haut).
        const plusProche = (i) => connus.reduce((m, c) => (Math.abs(c - i) < Math.abs(m - i) ? c : m), connus[0]);
        const cand = [];
        trous.forEach((i) => {
            const c = plusProche(i);
            const add = ratAdd(t.bas[c], ratSub(t.haut[i], t.haut[c]));
            if (ratVal(add) > 0 && nbDecimales(add) <= 2) cand.push({ v: add, code: 'additif' });
        });
        trous.forEach((i) => cand.push({ v: t.haut[i], code: 'recopie' }));
        const phrase = trous.map((i, j) => `$${ctx.ancre(decTex(nb(t.haut[i])))}{{${mots[j]}}}${T(` ${ctx.ub}`)}$`).join('\n');
        // ni un mot attendu, ni une valeur déjà écrite dans la ligne du bas
        const visibles = connus.map((i) => t.bas[i]);
        const surs = cand.filter((c) => !visibles.some((v) => ratEq(v, c.v)));
        const textes = filtrerDistracteurs(phrase, surs.map((c) => ecrire(c.v, ctx.ub)));
        const retenus = surs.filter((c, i, arr) => textes.includes(ecrire(c.v, ctx.ub))
            && arr.findIndex((d) => ecrire(d.v, ctx.ub) === ecrire(c.v, ctx.ub)) === i);
        // trois étiquettes, les deux familles présentes
        const additifs = retenus.filter((c) => c.code === 'additif');
        const recopies = retenus.filter((c) => c.code === 'recopie');
        if (!additifs.length || !recopies.length || retenus.length < 3) continue;
        const choisis = [additifs[0], recopies[0], ...shuffle([...additifs.slice(1), ...recopies.slice(1)]).slice(0, 1)];
        const distracteurs = choisis.map((c) => ecrire(c.v, ctx.ub));
        // rien ne se remplit par l'ordre seul : au moins deux affectations croissantes
        if (affectationsCroissantes(t.bas, trous, [...mots, ...distracteurs]) < 2) continue;
        const tableau = tableEnonce(
            ligneTable(ctx.a, t.haut.map((v) => ecrire(v, ctx.ua))),
            ligneTable(ctx.b, t.bas.map((v, i) => (trous.includes(i) ? '…' : ecrire(v, ctx.ub)))),
        );
        const payload = makeTexteTrou(phrase,
            `<p>${ctx.phrase}</p>\n${tableau}`
            + RAPPEL_BROUILLON
            + `<p><b>Complète les deux cases manquantes de la ligne « ${ctx.b} ».</b></p>`,
            { ...opts(L_QUATRIEME), mode: 'drag', distracteurs });
        payload._meta.params = { contexte: ctx.id, trous, distracteurs: choisis.map((c) => c.code) };
        return payload;
    }
    throw new Error('gen_172 deux cases : aucun tirage valide');
};

// ═══════════════════════════════════════════════════════════
// 3. PROBLÈME — quatrième proportionnelle en contexte (saisie)
// ═══════════════════════════════════════════════════════════

/** c n'est ni un multiple ni un diviseur de a (ni égal). */
const niMultipleNiDiviseur = (a, c) => {
    const q1 = ratDiv(c, a); const q2 = ratDiv(a, c);
    return !ratEq(a, c) && q1.q !== 1 && q2.q !== 1;
};

const CONTEXTES_PROBLEME = [
    {
        id: 'briques',
        tirer: () => {
            const u = R(randomChoice(['1,4', '1,6', '1,7', '1,8', '2,2', '2,4']));
            const a = ratN(randInt(4, 10)); const c = ratN(randInt(2, 15));
            const b = ratMul(u, a);
            return { a, b, c, x: ratMul(u, c),
                enonce: `${nb(a)} briques de masse identique pèsent ${nb(b)} kg.`,
                question: `Quelle est la masse de ${nb(c)} de ces briques ?`, suffixe: 'kg' };
        },
    },
    {
        id: 'farine',
        tirer: () => {
            const u = R(randomChoice(['1,2', '1,6', '2,4', '3,5']));
            const a = R(randomChoice(['1,5', '2', '2,5', '4']));
            const c = R(randomChoice(['0,5', '1,2', '1,8', '3,5', '4,5', '5']));
            const b = ratMul(u, a);
            return { a, b, c, x: ratMul(u, c),
                enonce: `${nb(a)} kg de farine ${coute(a)} ${euros(b)} €.`,
                question: `Combien ${coute(c)} ${nb(c)} kg de cette farine ?`, suffixe: '€' };
        },
    },
    {
        id: 'recette',
        tirer: () => {
            const u = R(randomChoice(['37,5', '42,5', '45', '62,5', '75']));
            const a = ratN(randomChoice([4, 6, 8])); const c = ratN(randomChoice([3, 5, 6, 10, 12]));
            const b = ratMul(u, a);
            return { a, b, c, x: ratMul(u, c),
                enonce: `Pour ${nb(a)} personnes, une recette demande ${nb(b)} g de farine.`,
                question: `Quelle masse de farine faut-il pour ${nb(c)} personnes ?`, suffixe: 'g' };
        },
    },
    {
        id: 'essence',
        tirer: () => {
            const c100 = R(randomChoice(['5,5', '6', '6,5', '7,2', '8']));
            const a = ratN(randomChoice([200, 250, 300, 400]));
            const c = ratN(randomChoice([50, 150, 350, 450, 500, 600, 700]));
            const u = ratDiv(c100, ratN(100));
            const b = ratMul(u, a);
            return { a, b, c, x: ratMul(u, c),
                enonce: `Une voiture consomme ${nb(b)} L d'essence pour ${nb(a)} km.`,
                question: `Quelle quantité d'essence faut-il pour ${nb(c)} km ?`, suffixe: 'L' };
        },
    },
];

const genContexteSaisie = () => {
    for (let essai = 0; essai < 120; essai++) {
        const ctx = randomChoice(CONTEXTES_PROBLEME);
        const d = ctx.tirer();
        if (!niMultipleNiDiviseur(d.a, d.c)) continue;
        // données à une décimale au plus, comme dans l'exemple (13,6 ; 2,5 ; 1,8) :
        // écarte 13,75 L, 16,25 L et 5,25 €, 8,75 € ; réponse à deux décimales au plus
        if (nbDecimales(d.b) > 1 || nbDecimales(d.x) > 2) continue;
        if (exempleDuCours([d.a, d.b, d.c])) continue;
        // second chemin : produit en croix b × c : a
        if (!ratEq(ratDiv(ratMul(d.b, d.c), d.a), d.x)) throw new Error('gen_172 problème : incohérent');
        const payload = makeSaisie(ecrire(d.x, d.suffixe),
            `<p>${d.enonce}</p>`
            + RAPPEL_BROUILLON
            + `<p><b>${d.question}</b></p>`,
            { ...opts(L_QUATRIEME), strict: false, suffixe: d.suffixe });
        payload._meta.params = { contexte: ctx.id, a: nb(d.a), b: nb(d.b), c: nb(d.c) };
        return payload;
    }
    throw new Error('gen_172 problème : aucun tirage valide');
};

// ═══════════════════════════════════════════════════════════
// 4. PROBLÈME EN QCM — « n1 objets identiques… combien pour n2 ? »
// ═══════════════════════════════════════════════════════════

const CONTEXTES_QCM = [
    { id: 'briques', unite: 'kg', us: ['1,4', '1,6', '1,7', '1,8', '2,2', '2,4'],
        enonce: (n1, M) => `${n1} briques de masse identique pèsent ${M} kg.`,
        question: (n2) => `Quelle est la masse de ${n2} de ces briques ?` },
    { id: 'cahiers', unite: '€', us: ['1,2', '1,5', '1,8', '2,4', '3,5'],
        enonce: (n1, M) => `${n1} cahiers identiques coûtent ${M} €.`,
        question: (n2) => `Combien coûtent ${n2} de ces cahiers ?` },
    { id: 'livres', unite: 'kg', us: ['0,4', '0,6', '0,8', '1,2'],
        enonce: (n1, M) => `${n1} livres identiques pèsent ${M} kg.`,
        question: (n2) => `Quelle est la masse de ${n2} de ces livres ?` },
    { id: 'bouteilles', unite: 'L', us: ['0,5', '1,5'],
        enonce: (n1, M) => `${n1} bouteilles identiques contiennent en tout ${M} L d'eau.`,
        question: (n2) => `Quel volume d'eau contiennent ${n2} de ces bouteilles ?` },
];

export const REGLES_DISTRACTEURS_QCM = {
    contexte_qcm_unique: {
        support: 'n1 objets identiques (n1 de 4 à 10) valent M = n1 × u (u à une décimale, jamais 1) ; on cherche la valeur de n2 objets (n2 de 2 à 15, ni multiple ni diviseur de n1, au-dessous ou au-dessus de n1) ; clé n2 × u',
        familles: {
            additif: 'M + (n2 − n1) : l\'écart du nombre d\'objets reporté sur la masse ou le prix (DEPP 4e 2023 q. 4, 15,1 % ; erreur principale, toujours présente)',
            une_etape: 'M ÷ n1 : la valeur d\'un objet, la multiplication oubliée (DEPP q. 4, 5,4 %)',
            somme_des_nombres: 'n1 + M + n2 : tous les nombres de l\'énoncé additionnés (DEPP q. 5, 6,9 %)',
            mauvais_diviseur: 'M ÷ n2 × n1 : la division par le mauvais nombre (inférence du cadrage), seulement quand elle tombe juste à deux décimales au plus',
        },
        supprimees: {},
        garantie: 'quatre choix : la clé, additif et deux familles tirées parmi les trois autres ; additif ≠ clé car u ≠ 1 ; une_etape ≠ clé car n2 ≠ 1 ; mauvais_diviseur ≠ clé car n1 ≠ n2 ; somme et toute égalité fortuite écartées par comparaison exacte, nouveau tirage borné ; valeurs toutes positives, deux à deux distinctes',
    },
    procedures_qcm_multiple: {
        support: 'n briques pèsent M = n × u (u à une décimale) ; on cherche la masse de p briques, p < n, avec des sous-masses faciles comme dans l\'exemple (a + b = p, a, b et q = n − p diviseurs de n, q ≠ p) ; cinq procédures, deux à quatre justes, nombre annoncé',
        justes: {
            une_brique_ou_coefficient: 'la masse d\'une brique (ou le coefficient de proportionnalité) M : n, puis × p',
            somme: 'la masse de a briques plus la masse de b briques',
            difference: 'la masse de n briques moins la masse de q briques (jamais q = p : circulaire)',
            directe: 'p × M : n',
        },
        familles: {
            mauvais_diviseur: 'la même phrase que la procédure « une brique » (ou « coefficient »), avec M : p puis × n',
            additif: '« Calculer n − p, puis retirer le résultat à M » : l\'écart du nombre de briques retiré à la masse',
            une_etape: 'M : n seul, la masse d\'une brique donnée pour réponse',
        },
        supprimees: {
            circulaire: 'l\'ancienne « retirer à la masse de n briques la masse de q briques » avec q = p (16,5 % des tirages) : il faut déjà la masse cherchée',
            m_moins_p: 'l\'ancienne « Calculer M − p » : elle donnait la bonne masse dans 2,1 % des tirages (M = 15, n = 10, p = 6)',
        },
        garantie: 'chaque procédure est recalculée en rationnels exacts : les justes donnent la clé (throw sinon), les fausses jamais (u ≠ 1, p ≠ 1, n ≠ p) ; phrases vraies et fausses sur les mêmes moules',
    },
};

// Rang de la clé parmi les quatre valeurs : visé uniformément de 1 à 4. Sans
// cela, une_etape (la valeur d'un objet) est presque toujours la plus petite et
// somme_des_nombres la plus grande : la clé tombait au milieu dans 85 % des
// tirages. Le rang visé choisit la paire de familles complétant additif ; faute
// de paire qui le donne, nouveau tirage (borné), puis n'importe quelle paire.
const rangDe = (cle, valeurs) => 1 + valeurs.filter((v) => ratVal(v) < ratVal(cle)).length;

const genContexteQcm = () => {
    const rangVise = randInt(1, 4);
    for (let essai = 0; essai < 300; essai++) {
        const ctx = randomChoice(CONTEXTES_QCM);
        const u = R(randomChoice(ctx.us));
        const n1 = randInt(4, 10);
        const n2 = randInt(2, 15);
        if (!niMultipleNiDiviseur(ratN(n1), ratN(n2))) continue;
        const M = ratMul(u, ratN(n1));
        const cle = ratMul(u, ratN(n2));
        if (exempleDuCours([ratN(n1), M, ratN(n2)])) continue;
        const familles = {
            additif: ratAdd(M, ratN(n2 - n1)),
            une_etape: u,
            somme_des_nombres: ratAdd(ratAdd(ratN(n1), M), ratN(n2)),
            mauvais_diviseur: ratMul(ratDiv(M, ratN(n2)), ratN(n1)),
        };
        const utilisable = (v) => ratVal(v) > 0 && nbDecimales(v) <= 2 && !ratEq(v, cle);
        if (!utilisable(familles.additif)) continue;
        const autres = ['une_etape', 'somme_des_nombres', 'mauvais_diviseur'].filter((f) => utilisable(familles[f]));
        const paires = [];
        for (let i = 0; i < autres.length; i++) {
            for (let j = i + 1; j < autres.length; j++) {
                const codes = ['additif', autres[i], autres[j]];
                const valeurs = [cle, ...codes.map((c) => familles[c])];
                if (new Set(valeurs.map((v) => ratToDecFr(v))).size !== 4) continue;
                paires.push({ codes, valeurs, rang: rangDe(cle, valeurs) });
            }
        }
        const visees = paires.filter((q) => q.rang === rangVise);
        const retenue = visees.length ? randomChoice(visees) : (essai >= 200 && paires.length ? randomChoice(paires) : null);
        if (!retenue) continue;
        // second chemin : produit en croix
        if (!ratEq(ratDiv(ratMul(M, ratN(n2)), ratN(n1)), cle)) throw new Error('gen_172 QCM : clé incohérente');
        const affiche = (v) => `${ecrire(v, ctx.unite)} ${ctx.unite}`;
        const payload = makeQcm(retenue.valeurs.map(affiche), affiche(cle),
            `<p>${ctx.enonce(n1, ecrire(M, ctx.unite))}</p>`
            + RAPPEL_BROUILLON
            + `<p><b>${ctx.question(n2)}</b></p>`,
            opts(L_QUATRIEME));
        payload._meta.params = { contexte: ctx.id, n1, n2, u: nb(u), rang: retenue.rang, distracteurs: retenue.codes };
        return payload;
    }
    throw new Error('gen_172 QCM : aucun tirage valide');
};

// ═══════════════════════════════════════════════════════════
// 5. ÉCHELLE — carte (deux sens : carte → réel, réel → carte)
// ═══════════════════════════════════════════════════════════

const genEchelleSaisie = () => {
    // Sur une carte, c1 cm ↔ r1 km : k = r1 ÷ c1 km par cm, entier.
    const k = randomChoice([5, 8, 10, 15, 20]);
    const c1 = randomChoice([2, 3, 4]);
    const r1 = k * c1;
    const enonce = `<p>Sur une carte, ${c1} cm représentent ${r1} km.</p>` + RAPPEL_BROUILLON;
    if (randInt(0, 1) === 0) {
        const c2 = randomChoice([6, 8, 10, 12]);
        const payload = makeSaisie(String(k * c2),
            `${enonce}<p><b>Quelle distance réelle correspond à ${c2} cm sur la carte ?</b></p>`,
            { ...opts(L_QUATRIEME), strict: false, suffixe: 'km' });
        payload._meta.params = { sens: 'carte_vers_reel', k, c1, c2 };
        return payload;
    }
    const cm = randomChoice([5, 7, 9, 11]);
    const payload = makeSaisie(String(cm),
        `${enonce}<p><b>Quelle longueur sur la carte correspond à ${k * cm} km en réalité ?</b></p>`,
        { ...opts(L_QUATRIEME), strict: false, suffixe: 'cm' });
    payload._meta.params = { sens: 'reel_vers_carte', k, c1, cm };
    return payload;
};

// ═══════════════════════════════════════════════════════════
// 6. POURCENTAGE — appliquer un pourcentage, ou le retrouver
// ═══════════════════════════════════════════════════════════

// p hors de { 100, 50, 25, 10, 1 } (automatisme 1128) et jamais « 20 % de 120 » (1129).
const P_DIRECT = [5, 15, 30, 40, 60, 75];
const POURCENTAGE_DIRECT = [
    { id: 'college', totaux: [300, 400, 480, 520, 600, 640, 720, 800],
        texte: (N, p) => `Un collège compte ${formatFr(N)} élèves. ${p} % d'entre eux sont demi-pensionnaires.`,
        question: 'Combien d\'élèves sont demi-pensionnaires ?', suffixe: 'élèves' },
    { id: 'commune', totaux: [1200, 2400, 3600, 4800, 6000, 8400],
        texte: (N, p) => `Une commune compte ${formatFr(N)} habitants. ${p} % d'entre eux ont moins de 20 ans.`,
        question: 'Combien d\'habitants ont moins de 20 ans ?', suffixe: 'habitants' },
    { id: 'vote', totaux: [400, 800, 1200, 1600, 2000],
        texte: (N, p) => `Lors d'une élection, ${formatFr(N)} personnes ont voté. Une candidate a obtenu ${p} % des voix.`,
        question: 'Combien de voix cette candidate a-t-elle obtenues ?', suffixe: 'voix' },
];
const POURCENTAGE_INVERSE = [
    { id: 'classe', totaux: [20, 25],
        texte: (N, n) => `Dans une classe de ${N} élèves, ${n} élèves viennent au collège à vélo.`,
        question: 'Quel pourcentage des élèves de la classe vient à vélo ?' },
    { id: 'college', totaux: [200, 400, 500, 800],
        texte: (N, n) => `Un collège compte ${formatFr(N)} élèves, dont ${formatFr(n)} font partie d'un club sportif.`,
        question: 'Quel pourcentage des élèves du collège fait partie d\'un club sportif ?' },
];

const genPourcentageSaisie = () => {
    if (randInt(0, 1) === 0) {
        for (let essai = 0; essai < 60; essai++) {
            const ctx = randomChoice(POURCENTAGE_DIRECT);
            const N = randomChoice(ctx.totaux);
            const p = randomChoice(P_DIRECT);
            const x = ratDiv(ratMul(ratN(N), ratN(p)), ratN(100));
            if (x.q !== 1) continue;                                        // un nombre de personnes est entier
            const payload = makeSaisie(formatFr(String(x.p)),
                `<p>${ctx.texte(N, p)}</p>` + RAPPEL_BROUILLON + `<p><b>${ctx.question}</b></p>`,
                { ...opts(L_QUATRIEME), strict: false, suffixe: ctx.suffixe });
            payload._meta.params = { sens: 'appliquer', contexte: ctx.id, N, p };
            return payload;
        }
        throw new Error('gen_172 pourcentage : aucun tirage valide');
    }
    for (let essai = 0; essai < 60; essai++) {
        const ctx = randomChoice(POURCENTAGE_INVERSE);
        const N = randomChoice(ctx.totaux);
        const n = randInt(2, N - 1);                                           // « 2 élèves viennent », jamais « 1 élèves »
        const p = ratDiv(ratMul(ratN(n), ratN(100)), ratN(N));
        if (p.q !== 1) continue;
        // pourcentages « ronds » écartés (automatismes), erreurs visées tenues distinctes de la clé
        if ([10, 20, 25, 50, 75, 100].includes(p.p) || p.p < 5 || p.p > 95) continue;
        if (n === p.p || 100 - p.p === p.p) continue;                        // nombre_pour_pourcentage, complementaire
        const payload = makeSaisie(String(p.p),
            `<p>${ctx.texte(N, n)}</p>` + RAPPEL_BROUILLON + `<p><b>${ctx.question}</b></p>`,
            { ...opts(L_QUATRIEME), strict: false, suffixe: '%' });
        payload._meta.params = { sens: 'retrouver', contexte: ctx.id, N, n,
            erreurs_visees: { nombre_pour_pourcentage: n, complementaire: 100 - p.p } };
        return payload;
    }
    throw new Error('gen_172 pourcentage inverse : aucun tirage valide');
};

// ═══════════════════════════════════════════════════════════
// 7. PROCÉDURES — QCM multiple (les briques de l'exemple)
// ═══════════════════════════════════════════════════════════

// (n, p) avec des sous-masses faciles, comme 2 + 4 = 6 et 8 − 2 pour 8 briques :
// a + b = p, a, b et q = n − p diviseurs de n, q ≠ p (jamais circulaire).
const PROCEDURES_NP = [
    { n: 8, p: 6, a: 2, b: 4 }, { n: 12, p: 9, a: 3, b: 6 }, { n: 12, p: 10, a: 4, b: 6 },
    { n: 12, p: 8, a: 2, b: 6 }, { n: 6, p: 5, a: 2, b: 3 }, { n: 6, p: 4, a: 1, b: 3 },
    { n: 16, p: 12, a: 4, b: 8 }, { n: 20, p: 15, a: 5, b: 10 }, { n: 4, p: 3, a: 1, b: 2 },
];
const U_BRIQUES = ['1,4', '1,6', '1,7', '1,8', '2,2', '2,4', '2,6'];

const genProceduresQcm = () => {
    const np = randomChoice(PROCEDURES_NP);
    const { n, p, a, b } = np;
    const q = n - p;
    if (a + b !== p || n % a || n % b || n % q || q === p) throw new Error('gen_172 procédures : sous-masses');
    const u = R(randomChoice(U_BRIQUES));
    const M = ratMul(u, ratN(n));
    const cle = ratMul(u, ratN(p));
    const Mt = decTex(nb(M));
    const brq = (x) => (x === 1 ? 'd\'une brique' : `de ${x} briques`);
    const parCoefficient = randInt(0, 1) === 0;
    // Chaque procédure : sa phrase et sa valeur recalculée (second chemin).
    const justes = [
        parCoefficient
            ? { t: `Calculer le coefficient de proportionnalité, ${tex(`${Mt} : ${n}`)}, puis multiplier ${p} par ce coefficient.`, v: ratMul(ratDiv(M, ratN(n)), ratN(p)) }
            : { t: `Calculer la masse d'une brique, ${tex(`${Mt} : ${n}`)}, puis la multiplier par ${p}.`, v: ratMul(ratDiv(M, ratN(n)), ratN(p)) },
        { t: `Additionner la masse ${brq(a)} et la masse ${brq(b)}.`, v: ratAdd(ratMul(u, ratN(a)), ratMul(u, ratN(b))) },
        { t: `Calculer la différence de la masse ${brq(n)} et de la masse ${brq(q)}.`, v: ratSub(M, ratMul(u, ratN(q))) },
        { t: `Calculer directement ${tex(`${p} \\times ${Mt} : ${n}`)}.`, v: ratDiv(ratMul(ratN(p), M), ratN(n)) },
    ];
    const fausses = [
        parCoefficient
            ? { t: `Calculer le coefficient de proportionnalité, ${tex(`${Mt} : ${p}`)}, puis multiplier ${n} par ce coefficient.`, v: ratMul(ratDiv(M, ratN(p)), ratN(n)), code: 'mauvais_diviseur' }
            : { t: `Calculer la masse d'une brique, ${tex(`${Mt} : ${p}`)}, puis la multiplier par ${n}.`, v: ratMul(ratDiv(M, ratN(p)), ratN(n)), code: 'mauvais_diviseur' },
        { t: `Calculer ${tex(`${n} - ${p}`)}, puis retirer le résultat à ${nb(M)}.`, v: ratSub(M, ratN(n - p)), code: 'additif' },
        { t: `Calculer directement ${tex(`${Mt} : ${n}`)}.`, v: ratDiv(M, ratN(n)), code: 'une_etape' },
    ];
    if (justes.some((j) => !ratEq(j.v, cle))) throw new Error('gen_172 procédures : une procédure juste ne donne pas la clé');
    if (fausses.some((f) => ratEq(f.v, cle))) throw new Error('gen_172 procédures : une procédure fausse donne la clé');
    // Deux à quatre justes sur cinq. Les options affichées sont re-choisies
    // (bornées) tant que les justes sont exactement les plus longues, ou
    // exactement les plus courtes, du texte lu : la longueur ne doit rien dire.
    const nbJustes = randInt(2, 4);
    const lue = (t) => t.replace(/\$\\mathrm\{\}/g, '').replace(/\$/g, '').replace(/\{,\}/g, ',').replace(/\\times/g, '×').length;
    let retenuesJustes = []; let retenuesFausses = [];
    for (let essai = 0; essai < 40; essai++) {
        retenuesJustes = shuffle(justes).slice(0, nbJustes);
        retenuesFausses = shuffle(fausses).slice(0, 5 - nbJustes);
        const lj = retenuesJustes.map((o) => lue(o.t)); const lf = retenuesFausses.map((o) => lue(o.t));
        if (!(Math.min(...lj) > Math.max(...lf)) && !(Math.max(...lj) < Math.min(...lf))) break;
    }
    const payload = makeQcm(
        shuffle([...retenuesJustes, ...retenuesFausses]).map((o) => o.t),
        retenuesJustes.map((o) => o.t),
        `<p>${n} briques de masse identique pèsent ${nb(M)} kg. On cherche la masse de ${p} de ces briques.</p>`
        + RAPPEL_BROUILLON
        + `<p><b>Sélectionne les ${nbJustes} procédures qui donnent la masse de ${p} briques.</b></p>`,
        opts(L_QUATRIEME), true);
    payload._meta.params = { n, p, M: nb(M), nbJustes, distracteurs: retenuesFausses.map((f) => f.code) };
    return payload;
};

// ═══════════════════════════════════════════════════════════
// 8. RELIER — une colonne complète, trois valeurs à relier, deux leurres
// ═══════════════════════════════════════════════════════════

const genRelier = () => {
    for (let essai = 0; essai < 150; essai++) {
        const ctx = randomChoice(CONTEXTES_TABLEAU);
        const k = R(randomChoice(ctx.coeffs));
        const vals = shuffle(ctx.vals).slice(0, 4).map(R);
        const [ref, ...sources] = vals;
        const refImage = ratMul(k, ref);
        const images = sources.map((v) => ratMul(k, v));
        if ([refImage, ...images].some((v) => nbDecimales(v) > 2)) continue;
        if (exempleDuCours([ref, refImage, ...sources.slice(0, 1)])) continue;
        // leurres : additif (image de référence + écart des sources) et recopie
        // (la source recopiée dans l'autre unité), pour deux sources différentes
        const [s1, s2] = shuffle([0, 1, 2]).slice(0, 2);
        const additif = ratAdd(refImage, ratSub(sources[s1], ref));
        const recopie = sources[s2];
        const leurres = [{ v: additif, code: 'additif' }, { v: recopie, code: 'recopie' }];
        const tous = [...images, additif, recopie];
        if (leurres.some((l) => ratVal(l.v) <= 0 || nbDecimales(l.v) > 2)) continue;
        if (new Set(tous.map((v) => ecrire(v, ctx.ub))).size !== 5) continue;
        // un leurre au moins entre la plus petite et la plus grande vraie cible :
        // ni les trois plus petites ni les trois plus grandes cibles ne sont les bonnes
        const mini = Math.min(...images.map(ratVal)); const maxi = Math.max(...images.map(ratVal));
        if (!leurres.some((l) => ratVal(l.v) > mini && ratVal(l.v) < maxi)) continue;
        // second chemin : chaque cible par produit en croix sur la colonne donnée
        images.forEach((img, i) => {
            if (!ratEq(ratDiv(ratMul(refImage, sources[i]), ref), img)) throw new Error('gen_172 relier : cible incohérente');
        });
        const etiquette = (v, unite) => `${ecrire(v, unite)} ${unite}`;
        const pairs = sources.map((v, i) => ({ source: etiquette(v, ctx.ua), target: etiquette(images[i], ctx.ub) }));
        const cibles = leurres.map((l) => ({ target: etiquette(l.v, ctx.ub), code: l.code }));
        const payload = makeRelier(shuffle(pairs),
            `<p>${ctx.colonne(ecrire(ref, ctx.ua), ecrire(refImage, ctx.ub), ref)}</p>`
            + RAPPEL_BROUILLON
            + `<p><b>${ctx.relie}</b></p>`,
            { ...opts(L_QUATRIEME), cibles_leurres: cibles });
        payload._meta.params = {
            contexte: ctx.id, reference: `${nb(ref)} ${ctx.ua} → ${ecrire(refImage, ctx.ub)} ${ctx.ub}`,
            distracteurs: Object.fromEntries(cibles.map((l) => [l.target, l.code])),
        };
        return payload;
    }
    throw new Error('gen_172 relier : aucun tirage valide');
};

// ═══════════════════════════════════════════════════════════
// REGISTRE
// ═══════════════════════════════════════════════════════════

const GENERATORS = {
    tableau_texte: genTableauSaisie,
    tableau_texte_trou: genTableauTrou,
    contexte_texte: genContexteSaisie,
    contexte_qcm_unique: genContexteQcm,
    echelle_texte: genEchelleSaisie,
    pourcentage_texte: genPourcentageSaisie,
    procedures_qcm_multiple: genProceduresQcm,
    quatrieme_relier: genRelier,
};

export const CATEGORIES = [
    {
        id: 'quatrieme',
        label: L_QUATRIEME,
        formatsList: [
            { key: 'tableau_texte', label: 'Tableau (saisie)' },
            { key: 'tableau_texte_trou', label: 'Tableau (deux cases)' },
            { key: 'contexte_texte', label: 'Problème (saisie)' },
            { key: 'contexte_qcm_unique', label: 'Problème (QCM)' },
            { key: 'echelle_texte', label: 'Échelle' },
            { key: 'pourcentage_texte', label: 'Pourcentage' },
            { key: 'procedures_qcm_multiple', label: 'Procédures (QCM multiple)' },
            { key: 'quatrieme_relier', label: 'Relier' },
        ],
    },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
